// src/components/Stats.js
import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

const stats = [
  {
    value: 330000,
    suffix: '+',
    label: 'Servers',
    description: 'Active servers worldwide',
    color: 'from-blue-500/20 to-transparent'
  },
  {
    value: 3000,
    suffix: '+',
    label: 'Cities',
    description: 'Global locations available',
    color: 'from-purple-500/20 to-transparent'
  },
  {
    value: 190,
    suffix: '+',
    label: 'Countries',
    description: 'International coverage',
    color: 'from-accent/20 to-transparent'
  },
  {
    value: 1000000,
    suffix: '+',
    label: 'IPs',
    description: 'Unique IP addresses',
    color: 'from-green-500/20 to-transparent'
  }
];

const AnimatedCounter = ({ value, suffix, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  useEffect(() => {
    if (inView) {
      let start = 0;
      const end = value;
      const incrementTime = duration / end;
      const counter = setInterval(() => {
        start += end / 50;
        if (start > end) {
          setCount(end);
          clearInterval(counter);
        } else {
          setCount(Math.floor(start));
        }
      }, incrementTime);

      return () => clearInterval(counter);
    }
  }, [value, duration, inView]);

  return (
    <span ref={ref} className="tabular-nums">
      {count.toLocaleString()}{suffix}
    </span>
  );
};

const StatCard = ({ stat, index }) => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  return (
    <div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="relative group"
    >
      <div className="glass-effect p-6 md:p-8 rounded-2xl hover:border-accent/50 transition-all duration-300">
        <div className="relative z-10">
          <h3 className="text-3xl md:text-4xl font-bold text-accent mb-2">
            <AnimatedCounter value={stat.value} suffix={stat.suffix} />
          </h3>
          <div className="text-xl md:text-2xl font-semibold text-text-primary mb-2">
            {stat.label}
          </div>
          <p className="text-text-secondary text-sm md:text-base">
            {stat.description}
          </p>
        </div>

        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl -z-10">
          <div className={`absolute inset-0 bg-gradient-to-br ${stat.color}`} />
        </div>
      </div>
    </div>
  );
};

const Stats = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  return (
    <section className="py-16 md:py-24 relative" id="stats" ref={ref}>
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 bg-accent/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-1/2 h-1/2 bg-purple-500/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            NexaPx in <span className="text-accent">Numbers</span>
          </h2>
          <p className="text-text-secondary text-sm md:text-base max-w-2xl mx-auto">
            "Without experimenting with these proxies, you'll never discover the hero within."
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-7xl mx-auto">
          {stats.map((stat, index) => (
            <StatCard key={index} stat={stat} index={index} />
          ))}
        </div>

      </div>
    </section>
  );
};

export default Stats;